"use client";

import { isValidPhoneNumber,parsePhoneNumber } from 'libphonenumber-js';
import Image from 'next/image';
import React, { useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import { useLanguage } from '@/contexts/LanguageContext';

export default function IletisimPage() {
  const { t, locale } = useLanguage();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Form validation
  const validateForm = () => {
    // İsim kontrolü
    if (!formData.name.trim()) {
      toast.error('Lütfen adınızı girin.');
      return false;
    }

    if (formData.name.trim().length < 2) {
      toast.error('Ad en az 2 karakter olmalıdır.');
      return false;
    }

    // Telefon kontrolü
    if (!formData.phone.trim()) {
      toast.error('Lütfen telefon numaranızı girin.');
      return false;
    }

    // Telefon numarası validasyonu
    try {
      const phoneNumber = parsePhoneNumber(formData.phone);
      if (!phoneNumber || !isValidPhoneNumber(formData.phone)) {
        toast.error('Lütfen geçerli bir telefon numarası girin. (Örn: +90 532 123 45 67)');
        return false;
      }
    } catch (error) {
      toast.error('Lütfen geçerli bir telefon numarası girin. (Örn: +90 532 123 45 67)');
      return false;
    }

    // Mesaj kontrolü
    if (!formData.message.trim()) {
      toast.error('Lütfen mesajınızı yazın.');
      return false;
    }

    if (formData.message.trim().length < 10) {
      toast.error('Mesaj en az 10 karakter olmalıdır.');
      return false;
    }

    return true;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validasyonu
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          locale: 'contact-tr' // tr/iletisim sayfası için özel işaretleme
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', message: '' }); // Form sıfırla
        toast.success('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
      } else {
        setSubmitStatus('error');
        const errorData = await response.json();
        toast.error(errorData.message || 'Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      setSubmitStatus('error');
      toast.error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Telefon input'u için özel işlem
    if (name === 'phone') {
      // Sadece rakam, +, boşluk ve () karakterlerine izin ver
      const phoneValue = value.replace(/[^0-9+\s()-]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: phoneValue
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-12'>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 5000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <div className='max-w-7xl mx-auto px-4 sm:px-6 mt-24'>
        <h1 className='text-3xl sm:text-4xl font-bold text-[#181C32] mb-2 text-center'>
          {t.homepage.contact.title}
        </h1>
        <p className='text-gray-500 mb-8 text-center'>
          {t.homepage.contact.subtitle}
        </p>
        {/* Clinic Images Row */}
        <div className='flex flex-col sm:flex-row gap-4 mb-8 justify-center items-center'>
          <div className='relative w-full sm:w-1/2 h-56 sm:h-96'>
            <Image
              src='/images/bariscinklinik1.webp'
              alt='Muayenehane Fotoğrafı 1'
              fill
              className='rounded-2xl shadow-lg object-cover'
              sizes='(max-width: 768px) 100vw, 50vw'
              priority
            />
          </div>
          <div className='relative w-full sm:w-1/2 h-56 sm:h-96'>
            <Image
              src='/images/bariscinklinik2.webp'
              alt='Muayenehane Fotoğrafı 2'
              fill
              className='rounded-2xl shadow-lg object-cover'
              sizes='(max-width: 768px) 100vw, 50vw'
              priority
            />
          </div>
        </div>
        {/* Info Grid */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-12'>
          {/* Address */}
          <a
            href="https://www.google.com/maps/place/Op.+Dr.+Bar%C4%B1%C5%9F+%C3%87in+(Estetik+Cerrahi)/@41.0093829,29.040065,19z/data=!4m6!3m5!1s0x14cac7d0beb6d8c9:0xdbd87ca94989a224!8m2!3d41.0092716!4d29.040871!16s%2Fg%2F11q9786szd?entry=ttu&g_ep=EgoyMDI1MDYwNC4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className='flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 h-full text-center hover:shadow-xl transition-shadow cursor-pointer group'
          >
            <span className='mb-3 text-orange-400 group-hover:text-orange-500 transition-colors'>
              <svg
                className='w-8 h-8'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M17.657 16.657L13.414 12.414a4 4 0 10-1.414 1.414l4.243 4.243a1 1 0 001.414-1.414z'
                />
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                />
              </svg>
            </span>
            <div className='font-bold text-[#181C32] mb-1 group-hover:text-blue-600 transition-colors'>{t.homepage.contact.address}</div>
            <div className='text-gray-700 text-sm group-hover:text-gray-900 transition-colors'>
              {t.homepage.contact.addressValue}
            </div>
          </a>
          {/* Contact */}
          <div className='flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 h-full text-center'>
            <span className='mb-3 text-orange-400'>
              <svg
                className='w-8 h-8'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                />
              </svg>
            </span>
            <div className='font-bold text-[#181C32] mb-1'>{t.homepage.contact.title}</div>
            <div className='text-gray-700 text-sm'>
              {t.homepage.contact.phone}:{' '}
              <a 
                href={`https://wa.me/905438999586?text=${encodeURIComponent(t.whatsapp.appointmentMessage)}`}
                target="_blank" 
                rel="noopener noreferrer"
                className='text-blue-600 hover:text-blue-800 transition-colors'
              >
                +90 543 899 95 86
              </a>
            </div>
            <div className='text-gray-700 text-sm'>
              {t.homepage.contact.email}:{' '}
              <span>{t.homepage.contact.emailValue}</span>
            </div>
          </div>
          {/* Hours */}
          <div className='flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 h-full text-center'>
            <span className='mb-3 text-orange-400'>
              <svg
                className='w-8 h-8'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 8v4l3 3'
                />
                <circle cx='12' cy='12' r='10' />
              </svg>
            </span>
            <div className='font-bold text-[#181C32] mb-1'>
              {t.homepage.contact.workingHours}
            </div>
            <div className='text-gray-700 text-sm'>
              {t.homepage.contact.workingHoursValue}
            </div>
          </div>
        </div>
        {/* Map */}
        <div className='rounded-2xl overflow-hidden shadow-lg min-h-[350px] mb-8'>
          <iframe
            src='https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12047.462513122592!2d29.0403156!3d40.9871771!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac9b409123457%3A0xa12345678bcdef01!2sKo%C5%9Fuyolu%2C%20Mehmet%20Akfan%20Sk.%20No%3A19-21%2C%2034718%20Kad%C4%B1k%C3%B6y%2F%C4%B0stanbul!5e0!3m2!1str!2str!4v1716741000000!5m2!1str!2str'
            width='100%'
            height='400'
            style={{ minHeight: 350, border: 0 }}
            allowFullScreen={true}
            loading='lazy'
            referrerPolicy='no-referrer-when-downgrade'
            title='Google Maps'
          ></iframe>
        </div>
        {/* Contact Form */}
        <section className='max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20'>
          <div className='bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 text-white shadow-lg'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 items-center'>
              <div>
                <h2 className='text-2xl sm:text-3xl font-bold mb-4'>
                  {t.homepage.contact.appointment}
                </h2>
                <p className='text-lg mb-6'>
                  {t.homepage.contact.appointmentDescription}
                </p>
                <div className='space-y-4'>
                  <a 
                    href={`https://wa.me/905438999586?text=${encodeURIComponent(t.whatsapp.appointmentMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='flex items-center gap-3 hover:text-blue-200 transition-colors cursor-pointer'
                  >
                    <svg
                      className='w-6 h-6'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                      />
                    </svg>
                    <span>+90 543 899 95 86</span>
                  </a>
                  <div className='flex items-center gap-3'>
                    <svg
                      className='w-6 h-6'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                      />
                    </svg>
                    <span>{t.homepage.contact.emailValue}</span>
                  </div>
                </div>
              </div>
              <div className='bg-white/10 rounded-2xl p-6 backdrop-blur-sm'>
                <form className='space-y-4' onSubmit={handleSubmit}>
                  <div>
                    <input
                      type='text'
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t.homepage.contact.name}
                      required
                      disabled={isSubmitting}
                      className='w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50'
                    />
                  </div>
                  <div>
                    <input
                      type='tel'
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Telefon (+90 532 123 45 67)"
                      required
                      disabled={isSubmitting}
                      className='w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50'
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t.homepage.contact.message}
                      rows={4}
                      required
                      disabled={isSubmitting}
                      className='w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 disabled:opacity-50'
                    ></textarea>
                  </div>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full bg-white text-blue-600 font-semibold py-2 px-4 rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {isSubmitting ? 'Gönderiliyor...' : t.homepage.contact.send}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
} 